import { blogPostItalianMetadata } from "./blogPostItalianMetadata";
import { blogPostSpanishMetadata } from "./blogPostSpanishMetadata";

export interface BlogPost {
  slug: string;
  title: string;
  titleDe?: string;
  titleFr?: string;
  titleIt?: string;
  date: string;
  /** ISO calendar date (YYYY-MM-DD) the article's content was last
   *  substantively modified; absent when never modified after publication.
   *  Feeds the sitemap's `lastmod` and the BlogPosting JSON-LD `dateModified`
   *  through {@link getPostModifiedDate} — edit it whenever an article's
   *  content genuinely changes. */
  updated?: string;
  readTime: string;
  readTimeDe?: string;
  readTimeFr?: string;
  readTimeIt?: string;
  summary: string;
  summaryDe?: string;
  summaryFr?: string;
  summaryIt?: string;
  tags: string[];
  tagsDe?: string[];
  tagsFr?: string[];
  tagsIt?: string[];
  author: string;
  authorRole: string;
  /** Original path from old website, used for SEO redirect preservation */
  legacyPath?: string;
  /** Category for filtering on the blog listing page */
  category: "Technology" | "Business" | "Technology Management";
  categoryDe?: string;
  categoryFr?: string;
  categoryIt?: string;
  /** Hero image path relative to src/assets/blog/ (filename only) */
  heroImage: string;
}

const ITALIAN_CATEGORY_MAP: Record<BlogPost["category"], string> = {
  Technology: "Tecnologia",
  Business: "Business",
  "Technology Management": "Management tecnologico",
};

const SPANISH_CATEGORY_MAP: Record<BlogPost["category"], string> = {
  Technology: "Tecnología",
  Business: "Negocio",
  "Technology Management": "Gestión tecnológica",
};

export const blogPosts: BlogPost[] = [
  {
    slug: "how-to-increase-claude-code-generation-quality-by-20-percent",
    title: "Increase Claude Code Quality by 20%",
    titleDe: "Claude Code: 20 % mehr Code-Qualität",
    titleFr: "Comment augmenter de 20 % la qualité Claude Code",
    date: "2026-03-17",
    updated: "2026-09-09",
    readTime: "8 min read",
    readTimeDe: "10 Min. Lesezeit",
    readTimeFr: "8 min de lecture",
    summary:
      "AI coding agents drop to 20-30% accuracy on commercial codebases. Our open-source Agentic Suite fixes this with structured web search, dual-model code review, and enforced TDD.",
    summaryDe:
      "KI-Programmieragenten fallen bei kommerziellen Codebasen auf 20-30 % Genauigkeit. Unsere Open-Source-Agenten-Suite behebt dies mit Websuche, Dual-Modell-Code-Review und TDD.",
    summaryFr:
      "Les agents de codage IA tombent à 20-30 % de précision sur les bases de code commerciales. Notre suite agentique open source corrige ce problème grâce à la recherche web structurée, à la revue de code par double modèle et à un TDD strictement appliqué.",
    tags: ["AI Coding", "Claude Code", "MCP", "Open Source", "TDD"],
    tagsDe: ["KI-Programmierung", "Claude Code", "MCP", "Open Source", "TDD"],
    tagsFr: ["Codage IA", "Claude Code", "MCP", "Open Source", "TDD"],
    author: "Sergii Shelpuk",
    authorRole: "CEO",
    category: "Technology",
    categoryDe: "Technologie",
    categoryFr: "Technologie",
    heroImage: "claude-code-quality-hero.jpg",
  },
  {
    slug: "shelpuk-ai-technology-consulting-recognized-as-a-top-ai-consulting-company",
    title: "Named a Top AI Consulting Company by Vendorland",
    titleDe: "Als f\u00FChrendes KI-Beratungshaus ausgezeichnet",
    titleFr: "Reconnu parmi les meilleurs cabinets de conseil IA",
    date: "2025-02-10",
    updated: "2026-09-09",
    readTime: "1 min read",
    readTimeDe: "2 Min. Lesezeit",
    readTimeFr: "1 min de lecture",
    summary:
      "Shelpuk AI Technology Consulting has been named one of the top AI consulting companies by Vendorland for expertise in AI consulting, machine learning, and data analytics.",
    summaryDe:
      "Shelpuk AI Technology Consulting wurde von Vendorland als eines der f\u00FChrenden KI-Beratungsunternehmen f\u00FCr Expertise in KI-Beratung, Machine Learning und Datenanalyse ausgezeichnet.",
    summaryFr:
      "Vendorland a distingué Shelpuk AI Technology Consulting parmi les cabinets de conseil en IA de référence pour son expertise en conseil IA, en machine learning et en data analytics.",
    tags: ["Company News", "Awards", "Vendorland"],
    tagsDe: ["Unternehmensnachrichten", "Auszeichnungen", "Vendorland"],
    tagsFr: ["Actualités de l’entreprise", "Distinctions", "Vendorland"],
    author: "Sergii Shelpuk",
    authorRole: "Managing Partner",
    legacyPath: "post/shelpuk-ai-technology-consulting-recognized-as-a-top-ai-consulting-company",
    category: "Business",
    categoryDe: "Wirtschaft",
    categoryFr: "Entreprise",
    heroImage: "vendorland-recognition.png",
  },
  {
    slug: "double-your-llama-3-3-inference-speed-with-huggingface-text-generation-inference",
    title: "Double Llama 3.3 Speed with Hugging Face TGI",
    titleDe: "Llama 3.3 mit Hugging Face TGI beschleunigen",
    titleFr: "Llama 3.3 plus rapide avec Hugging Face TGI",
    date: "2025-01-06",
    updated: "2026-09-09",
    readTime: "7 min read",
    readTimeDe: "10 Min. Lesezeit",
    readTimeFr: "7 min de lecture",
    summary:
      "Deploy Llama 3.3 with Huggingface TGI to cut inference time in half compared to standard Transformers \u2014 a step-by-step guide with benchmarks on NVIDIA H100 GPUs.",
    summaryDe:
      "Setzen Sie Llama 3.3 mit Huggingface TGI ein, um die Inferenzzeit im Vergleich zu Standard-Transformers zu halbieren \u2014 eine Schritt-f\u00FCr-Schritt-Anleitung mit Benchmarks auf NVIDIA H100 GPUs.",
    summaryFr:
      "Déployez Llama 3.3 avec Hugging Face TGI pour diviser par deux le temps d'inférence par rapport à Transformers standard, avec un guide pas à pas et des benchmarks sur GPU NVIDIA H100.",
    tags: ["LLM", "Llama 3.3", "TGI", "Inference", "Deployment"],
    tagsDe: ["LLM", "Llama 3.3", "TGI", "Inferenz", "Deployment"],
    tagsFr: ["LLM", "Llama 3.3", "TGI", "Inférence", "Déploiement"],
    author: "Sergii Shelpuk",
    authorRole: "CEO",
    legacyPath: "post/double-your-llama-3-3-inference-speed-with-huggingface-text-generation-inference",
    category: "Technology",
    categoryDe: "Technologie",
    categoryFr: "Technologie",
    heroImage: "tgi-llama33-hero.png",
  },
  {
    slug: "buy-vs-build-in-ai-making-the-optimal-technology-choice",
    title: "Buy vs. Build: The Optimal AI Technology Choice",
    titleDe: "Buy vs. Build: Die optimale KI-Entscheidung",
    titleFr: "Buy vs. Build : le bon choix technologique en IA",
    date: "2024-11-11",
    updated: "2026-09-09",
    readTime: "4 min read",
    readTimeDe: "5 Min. Lesezeit",
    readTimeFr: "4 min de lecture",
    summary:
      "Should you buy AI services or build your own? A framework for the buy vs. build decision based on whether you\u2019re optimizing costs or building competitive advantage.",
    summaryDe:
      "Sollten Sie KI-Dienste kaufen oder selbst bauen? Ein Framework f\u00FCr die Buy-vs.-Build-Entscheidung, je nachdem ob Sie Kosten optimieren oder Wettbewerbsvorteile aufbauen.",
    summaryFr:
      "Faut-il acheter des services d'IA ou construire sa propre solution ? Un cadre d'analyse buy vs. build selon que vous cherchez à optimiser les coûts ou à créer un avantage concurrentiel.",
    tags: ["AI Strategy", "Buy vs Build", "Competitive Advantage"],
    tagsDe: ["KI-Strategie", "Buy vs. Build", "Wettbewerbsvorteil"],
    tagsFr: ["Stratégie IA", "Buy vs. Build", "Avantage concurrentiel"],
    author: "Sergii Shelpuk",
    authorRole: "CEO",
    legacyPath: "post/buy-vs-build-in-ai-making-the-optimal-technology-choice",
    category: "Technology Management",
    categoryDe: "Technologiemanagement",
    categoryFr: "Management technologique",
    heroImage: "buy-vs-build-hero.png",
  },
  {
    slug: "meet-llama-3-2-edge-llm-vision-agentic-capabilities-and-more",
    title: "Meet Llama 3.2: Edge LLM, Vision, and Agents",
    titleDe: "Llama 3.2: Edge-LLM, Vision und Agenten",
    titleFr: "Llama 3.2 : edge LLM, vision et agents",
    date: "2024-10-10",
    updated: "2026-09-09",
    readTime: "7 min read",
    readTimeDe: "8 Min. Lesezeit",
    readTimeFr: "7 min de lecture",
    summary:
      "Llama 3.2 brings multimodal capabilities, on-device AI, and agentic applications. Exploring what makes Meta\u2019s latest open-access model exceptional for business use.",
    summaryDe:
      "Llama 3.2 bringt multimodale F\u00E4higkeiten, On-Device-KI und agentische Anwendungen. Was Metas neuestes frei zug\u00E4ngliches Modell f\u00FCr den gesch\u00E4ftlichen Einsatz au\u00DFergew\u00F6hnlich macht.",
    summaryFr:
      "Llama 3.2 apporte des capacités multimodales, de l'IA embarquée et des usages agentiques. Décryptage de ce qui rend le dernier modèle open access de Meta particulièrement intéressant pour les entreprises.",
    tags: ["LLM", "Llama 3.2", "Vision", "Agentic AI", "Edge AI"],
    tagsDe: ["LLM", "Llama 3.2", "Vision", "Agentische KI", "Edge-KI"],
    tagsFr: ["LLM", "Llama 3.2", "Vision", "IA agentique", "IA embarquée"],
    author: "Sergii Shelpuk",
    authorRole: "CEO",
    legacyPath: "post/meet-llama-3-2-edge-llm-vision-agentic-capabilities-and-more",
    category: "Technology",
    categoryDe: "Technologie",
    categoryFr: "Technologie",
    heroImage: "llama-3-2-hero.png",
  },
  {
    slug: "so-you-want-to-explore-ai-for-your-company-where-to-start",
    title: "Exploring AI for Your Company? Where to Start",
    titleDe: "KI im Unternehmen erkunden \u2013 wo fangen Sie an?",
    titleFr: "Explorer l'IA en entreprise : par où commencer ?",
    date: "2024-09-30",
    updated: "2026-09-09",
    readTime: "5 min read",
    readTimeDe: "6 Min. Lesezeit",
    readTimeFr: "5 min de lecture",
    summary:
      "A practical framework for business executives exploring AI: consulting, solution design, or implementation \u2014 based on whether problem and solution are known.",
    summaryDe:
      "Ein praktisches Framework f\u00FCr Gesch\u00E4ftsf\u00FChrer, die KI erkunden: Beratung, L\u00F6sungsdesign oder Implementierung \u2013 je nachdem, ob Problem und L\u00F6sung bekannt sind.",
    summaryFr:
      "Un cadre pratique pour les dirigeants qui veulent explorer l'IA : conseil, conception de solution ou implémentation, selon que le problème et la solution sont déjà clarifiés.",
    tags: ["AI Strategy", "Consulting", "Business Strategy"],
    tagsDe: ["KI-Strategie", "Beratung", "Gesch\u00E4ftsstrategie"],
    tagsFr: ["Stratégie IA", "Conseil", "Stratégie d’entreprise"],
    author: "Sergii Shelpuk",
    authorRole: "CEO",
    legacyPath: "post/so-you-want-to-explore-ai-for-your-company-where-to-start",
    category: "Business",
    categoryDe: "Wirtschaft",
    categoryFr: "Entreprise",
    heroImage: "explore-ai-hero.png",
  },
  {
    slug: "fine-tuning-llama-3-1-with-swift-unsloth-alternative-for-multi-gpu-llm-training",
    title: "Fine-Tune Llama 3.1 with SWIFT: Multi-GPU LLM",
    titleDe: "Llama 3.1 mit SWIFT: Multi-GPU-Fine-Tuning",
    titleFr: "Fine-tuning de Llama 3.1 avec SWIFT : multi-GPU",
    date: "2024-09-15",
    updated: "2026-09-09",
    readTime: "8 min read",
    readTimeDe: "12 Min. Lesezeit",
    readTimeFr: "8 min de lecture",
    summary:
      "A step-by-step guide to fine-tuning Llama 3.1 using SWIFT on multi-GPU RunPod infrastructure, with LoRA configuration, dataset preprocessing, and completion-only training.",
    summaryDe:
      "Eine Schritt-f\u00FCr-Schritt-Anleitung zum Fine-Tuning von Llama 3.1 mit SWIFT auf Multi-GPU-RunPod-Infrastruktur, mit LoRA-Konfiguration, Datensatzvorverarbeitung und Completion-only-Training.",
    summaryFr:
      "Guide pas à pas pour fine-tuner Llama 3.1 avec SWIFT sur une infrastructure RunPod multi-GPU, avec configuration LoRA, prétraitement du dataset et entraînement completion-only.",
    tags: ["LLM", "SWIFT", "Fine-Tuning", "Multi-GPU", "Llama 3.1"],
    tagsDe: ["LLM", "SWIFT", "Fine-Tuning", "Multi-GPU", "Llama 3.1"],
    tagsFr: ["LLM", "SWIFT", "Fine-tuning", "Multi-GPU", "Llama 3.1"],
    author: "Sergii Shelpuk",
    authorRole: "CEO",
    legacyPath: "post/fine-tuning-llama-3-1-with-swift-unsloth-alternative-for-multi-gpu-llm-training",
    category: "Technology",
    categoryDe: "Technologie",
    categoryFr: "Technologie",
    heroImage: "swift-hero.png",
  },
  {
    slug: "how-to-fine-tune-llama-3-1-with-unsloth-twice-as-fast-and-using-70-less-gpu-memory",
    title: "Fine-Tune Llama 3.1 with Unsloth: 2x Faster",
    titleDe: "Llama 3.1 mit Unsloth: doppelt so schnell",
    titleFr: "Llama 3.1 avec Unsloth : deux fois plus vite",
    date: "2024-08-26",
    updated: "2026-09-09",
    readTime: "7 min read",
    readTimeDe: "10 Min. Lesezeit",
    readTimeFr: "7 min de lecture",
    summary:
      "A practical guide to installing and using Unsloth for efficient LLM fine-tuning on GPU cloud platforms, with best practices for Llama 3.1 prompt structure and completion-only training.",
    summaryDe:
      "Eine praktische Anleitung zur Installation und Nutzung von Unsloth f\u00FCr effizientes LLM-Fine-Tuning auf GPU-Cloud-Plattformen, mit Best Practices f\u00FCr die Llama-3.1-Prompt-Struktur und Completion-only-Training.",
    summaryFr:
      "Guide pratique pour installer et utiliser Unsloth afin de fine-tuner efficacement des LLM sur des plateformes cloud GPU, avec les bonnes pratiques pour la structure de prompts de Llama 3.1 et l'entraînement completion-only.",
    tags: ["LLM", "Unsloth", "Fine-Tuning", "Llama 3.1"],
    tagsDe: ["LLM", "Unsloth", "Fine-Tuning", "Llama 3.1"],
    tagsFr: ["LLM", "Unsloth", "Fine-tuning", "Llama 3.1"],
    author: "Sergii Shelpuk",
    authorRole: "CEO",
    legacyPath: "post/how-to-fine-tune-llama-3-1-with-unsloth-twice-as-fast-and-using-70-less-gpu-memory",
    category: "Technology",
    categoryDe: "Technologie",
    categoryFr: "Technologie",
    heroImage: "unsloth-hero.png",
  },
  {
    slug: "low-rank-adaptation-lora-for-efficient-fine-tuning-of-large-language-models",
    title: "Low-Rank Adaptation (LoRA) for Efficient LLMs",
    titleDe: "Low-Rank Adaptation (LoRA) f\u00FCr effiziente LLMs",
    titleFr: "Low-Rank Adaptation (LoRA) pour LLM efficaces",
    date: "2024-08-03",
    updated: "2026-09-09",
    readTime: "6 min read",
    readTimeDe: "6 Min. Lesezeit",
    readTimeFr: "6 min de lecture",
    summary:
      "Demystifying LoRA: a cutting-edge technique for fine-tuning large language models like Llama-2, Llama-3, and Mistral efficiently through low-rank matrix decomposition.",
    summaryDe:
      "LoRA entmystifiziert: eine hochmoderne Technik zum effizienten Fine-Tuning gro\u00DFer Sprachmodelle wie Llama-2, Llama-3 und Mistral durch Low-Rank-Matrixzerlegung.",
    summaryFr:
      "LoRA démystifié : une technique de pointe pour fine-tuner efficacement de grands modèles de langage comme Llama-2, Llama-3 et Mistral grâce à la décomposition matricielle de rang faible.",
    tags: ["LLM", "LoRA", "Fine-Tuning", "Machine Learning"],
    tagsDe: ["LLM", "LoRA", "Fine-Tuning", "Machine Learning"],
    tagsFr: ["LLM", "LoRA", "Fine-tuning", "Machine learning"],
    author: "Sergii Shelpuk",
    authorRole: "CEO",
    legacyPath: "post/low-rank-adaptation-lora-for-efficient-fine-tuning-of-large-language-models",
    category: "Technology",
    categoryDe: "Technologie",
    categoryFr: "Technologie",
    heroImage: "lora-hero.png",
  },
  {
    slug: "llm-practitioner-s-guide-gemma-a-game-changing-multilingual-llm",
    title: "LLM Practitioner\u2019s Guide: Multilingual Gemma",
    titleDe: "LLM Practitioner\u2019s Guide: Gemma, mehrsprachig",
    titleFr: "Guide du praticien LLM : Gemma, multilingue",
    date: "2024-02-27",
    updated: "2026-09-09",
    readTime: "10 min read",
    readTimeDe: "12 Min. Lesezeit",
    readTimeFr: "10 min de lecture",
    summary:
      "Gemma marks the arrival of the first truly multilingual chat LLM. Why this Google release is a game-changer for multilingual AI applications.",
    summaryDe:
      "Gemma markiert die Ankunft des ersten wirklich mehrsprachigen Chat-LLMs. Warum diese Google-Ver\u00F6ffentlichung ein Wendepunkt f\u00FCr mehrsprachige KI-Anwendungen ist.",
    summaryFr:
      "Gemma marque l'arrivée du premier véritable LLM conversationnel multilingue. Pourquoi cette sortie de Google change la donne pour les applications d'IA multilingues.",
    tags: ["LLM", "Gemma", "Multilingual", "Tokenizer"],
    tagsDe: ["LLM", "Gemma", "Mehrsprachigkeit", "Tokenizer"],
    tagsFr: ["LLM", "Gemma", "Multilingue", "Tokenizer"],
    author: "Sergii Shelpuk",
    authorRole: "CEO",
    legacyPath: "post/llm-practitioner-s-guide-gemma-a-game-changing-multilingual-llm",
    category: "Technology",
    categoryDe: "Technologie",
    categoryFr: "Technologie",
    heroImage: "gemma-multilingual-hero.png",
  },
  {
    slug: "llm-practitioner-s-guide-how-multilingual-falcon-mistral-smaug-and-other-llms-are",
    title: "LLM Practitioner\u2019s Guide: Falcon, Mistral, Smaug",
    titleDe: "LLM Practitioner\u2019s Guide: mehrsprachige LLMs",
    titleFr: "Guide du praticien LLM : Falcon, Mistral, Smaug",
    date: "2024-02-12",
    updated: "2026-09-09",
    readTime: "18 min read",
    readTimeDe: "18 Min. Lesezeit",
    readTimeFr: "18 min de lecture",
    summary:
      "Comprehensive tokenizer tests on six popular LLMs across 12 languages. Which models are the most adaptable for each language?",
    summaryDe:
      "Umfassende Tokenizer-Tests an sechs popul\u00E4ren LLMs in 12 Sprachen. Welche Modelle sind f\u00FCr jede Sprache am anpassungsf\u00E4higsten?",
    summaryFr:
      "Des tests complets de tokenizer sur six LLM populaires dans 12 langues. Quels modèles s'adaptent le mieux à chaque langue ?",
    tags: ["LLM", "Multilingual", "Tokenizer", "NLP"],
    tagsDe: ["LLM", "Mehrsprachigkeit", "Tokenizer", "NLP"],
    tagsFr: ["LLM", "Multilingue", "Tokenizer", "NLP"],
    author: "Sergii Shelpuk",
    authorRole: "CEO",
    legacyPath: "post/llm-practitioner-s-guide-how-multilingual-falcon-mistral-smaug-and-other-llms-are",
    category: "Technology Management",
    categoryDe: "Technologiemanagement",
    categoryFr: "Management technologique",
    heroImage: "multilingual-llms-hero.png",
  },
  {
    slug: "llm-practitioner-s-guide-how-multilingual-llama-2-actually-is",
    title: "LLM Practitioner\u2019s Guide: Multilingual Llama-2",
    titleDe: "LLM Practitioner\u2019s Guide: Llama-2 mehrsprachig",
    titleFr: "Guide du praticien LLM : Llama-2 multilingue",
    date: "2024-01-04",
    updated: "2026-09-09",
    readTime: "7 min read",
    readTimeDe: "7 Min. Lesezeit",
    readTimeFr: "7 min de lecture",
    summary:
      "Exploring Llama-2\u2019s most significant limitation: its restricted language support. Why extending Llama-2 beyond a few European languages is a challenging task.",
    summaryDe:
      "Untersuchung der gr\u00F6\u00DFten Einschr\u00E4nkung von Llama-2: die begrenzte Sprachunterst\u00FCtzung. Warum die Erweiterung von Llama-2 \u00FCber einige europ\u00E4ische Sprachen hinaus eine anspruchsvolle Aufgabe ist.",
    summaryFr:
      "Analyse de la principale limite de Llama-2 : sa prise en charge restreinte des langues. Pourquoi l'étendre au-delà de quelques langues européennes reste un défi.",
    tags: ["LLM", "Llama-2", "Multilingual", "NLP"],
    tagsDe: ["LLM", "Llama-2", "Mehrsprachigkeit", "NLP"],
    tagsFr: ["LLM", "Llama-2", "Multilingue", "NLP"],
    author: "Sergii Shelpuk",
    authorRole: "CEO",
    legacyPath: "post/llm-practitioner-s-guide-how-multilingual-llama-2-actually-is",
    category: "Technology",
    categoryDe: "Technologie",
    categoryFr: "Technologie",
    heroImage: "llama2-multilingual.png",
  },
  {
    slug: "shelpuk-ai-technology-consulting-honored-as-a-clutch-champion-and-clutch-global-for-2023",
    title: "Honored as a Clutch Champion and Clutch Global",
    titleDe: "Als Clutch Champion und Clutch Global geehrt",
    titleFr: "Distingué Clutch Champion et Clutch Global 2023",
    date: "2023-11-15",
    updated: "2026-09-09",
    readTime: "2 min read",
    readTimeDe: "2 Min. Lesezeit",
    readTimeFr: "2 min de lecture",
    summary:
      "We are proud to be named a 2023 Clutch Global and Clutch Champion winner, ranking among the top 10% of companies on Clutch.",
    summaryDe:
      "Wir sind stolz, als Clutch Global- und Clutch Champion-Gewinner 2023 ausgezeichnet worden zu sein und zu den Top 10\u00A0% der Unternehmen auf Clutch zu geh\u00F6ren.",
    summaryFr:
      "Nous sommes fiers d'avoir été distingués Clutch Global et Clutch Champion 2023, ce qui nous place parmi les 10 % d'entreprises les mieux classées sur Clutch.",
    tags: ["Company News", "Awards", "Clutch"],
    tagsDe: ["Unternehmensnachrichten", "Auszeichnungen", "Clutch"],
    tagsFr: ["Actualités de l’entreprise", "Distinctions", "Clutch"],
    author: "Sergii Shelpuk",
    authorRole: "CEO",
    legacyPath: "post/shelpuk-ai-technology-consulting-honored-as-a-clutch-champion-and-clutch-global-for-2023",
    category: "Business",
    categoryDe: "Wirtschaft",
    categoryFr: "Entreprise",
    heroImage: "clutch-awards-2023.png",
  },
  {
    slug: "the-tango-takes-two-building-synergy-between-ai-and-product-management",
    title: "Building Synergy Between AI and Product",
    titleDe: "Synergie zwischen KI und Produktmanagement",
    titleFr: "Créer une synergie entre IA et product management",
    date: "2023-07-04",
    updated: "2026-09-09",
    readTime: "6 min read",
    readTimeDe: "6 Min. Lesezeit",
    readTimeFr: "6 min de lecture",
    summary:
      "Why successful AI products require seamless collaboration between AI and product teams \u2014 and a practical checklist to ensure your virtuous AI cycle keeps spinning.",
    summaryDe:
      "Warum erfolgreiche KI-Produkte eine nahtlose Zusammenarbeit zwischen KI- und Produktteams erfordern \u2014 und eine praktische Checkliste, um sicherzustellen, dass Ihr tugendhafter KI-Kreislauf in Schwung bleibt.",
    summaryFr:
      "Pourquoi les produits IA réussis exigent une collaboration fluide entre les équipes IA et produit, avec une checklist concrète pour entretenir un cercle vertueux.",
    tags: ["AI Strategy", "Product Management", "Competitive Advantage"],
    tagsDe: ["KI-Strategie", "Produktmanagement", "Wettbewerbsvorteil"],
    tagsFr: ["Stratégie IA", "Product management", "Avantage concurrentiel"],
    author: "Sergii Shelpuk",
    authorRole: "Managing Partner",
    legacyPath: "post/the-tango-takes-two-building-synergy-between-ai-and-product-management",
    category: "Business",
    categoryDe: "Wirtschaft",
    categoryFr: "Entreprise",
    heroImage: "tango-takes-two-hero.png",
  },
  {
    slug: "competent-management-a-surprising-source-of-competitive-advantage",
    title: "Competent Management as a Competitive Advantage",
    titleDe: "Kompetentes Management als Wettbewerbsvorteil",
    titleFr: "Le management, un avantage concurrentiel",
    date: "2023-05-30",
    updated: "2026-09-09",
    readTime: "7 min read",
    readTimeDe: "9 Min. Lesezeit",
    readTimeFr: "7 min de lecture",
    summary:
      "Why competent management is an undervalued competitive advantage \u2014 and how vicious cycles of poor practices trap companies in mediocrity.",
    summaryDe:
      "Warum kompetentes Management ein untersch\u00E4tzter Wettbewerbsvorteil ist \u2014 und wie Teufelskreise schlechter Praktiken Unternehmen in der Mittelmäßigkeit gefangen halten.",
    summaryFr:
      "Pourquoi un management compétent constitue un avantage concurrentiel sous-estimé, et comment les cercles vicieux de mauvaises pratiques enferment les entreprises dans la médiocrité.",
    tags: ["Management", "Competitive Advantage", "Strategy"],
    tagsDe: ["Management", "Wettbewerbsvorteil", "Strategie"],
    tagsFr: ["Management", "Avantage concurrentiel", "Stratégie"],
    author: "Sergii Shelpuk",
    authorRole: "Managing Partner",
    legacyPath: "post/competent-management-a-surprising-source-of-competitive-advantage",
    category: "Business",
    categoryDe: "Wirtschaft",
    categoryFr: "Entreprise",
    heroImage: "competent-management-hero.png",
  },
  {
    slug: "can-chatgpt-build-a-competitive-advantage-for-your-company-and-if-so-how",
    title: "Can ChatGPT Build a Competitive Advantage?",
    titleDe: "Schafft ChatGPT einen Wettbewerbsvorteil?",
    titleFr: "ChatGPT crée-t-il un avantage concurrentiel ?",
    date: "2023-05-23",
    updated: "2026-09-09",
    readTime: "11 min read",
    readTimeDe: "11 Min. Lesezeit",
    readTimeFr: "11 min de lecture",
    summary:
      "Does ChatGPT offer any competitive advantages for your company? Can LLMs establish a sustainable competitive edge? A framework for thinking about the current AI hype.",
    summaryDe:
      "Bietet ChatGPT Ihrem Unternehmen Wettbewerbsvorteile? Können LLMs einen nachhaltigen Wettbewerbsvorteil schaffen? Ein Denkmodell für den aktuellen KI-Hype.",
    summaryFr:
      "ChatGPT peut-il réellement créer un avantage concurrentiel pour votre entreprise ? Un cadre pour distinguer l'effet de mode autour de l'IA des avantages durables.",
    tags: ["AI Strategy", "ChatGPT", "Competitive Advantage", "LLMs"],
    tagsDe: ["KI-Strategie", "ChatGPT", "Wettbewerbsvorteil", "LLMs"],
    tagsFr: ["Stratégie IA", "ChatGPT", "Avantage concurrentiel", "LLM"],
    author: "Sergii Shelpuk",
    authorRole: "Managing Partner",
    legacyPath: "post/can-chatgpt-build-a-competitive-advantage-for-your-company-and-if-so-how",
    category: "Business",
    categoryDe: "Wirtschaft",
    categoryFr: "Entreprise",
    heroImage: "chatgpt-competitive-advantage-hero.png",
  },
  {
    slug: "all-you-need-is-a-network-effect-how-great-tech-and-ai-products-compete",
    title: "Network Effects: How Tech and AI Products Win",
    titleDe: "Netzwerkeffekt: Wie Tech- und KI-Produkte gewinnen",
    titleFr: "Effet de réseau : comment les produits IA gagnent",
    date: "2023-05-16",
    updated: "2026-09-09",
    readTime: "7 min read",
    readTimeDe: "7 Min. Lesezeit",
    readTimeFr: "7 min de lecture",
    summary:
      "What sets apart the most valuable tech companies? Why platforms, social networks, marketplaces, and AI products all converge on a single phenomenon — the network effect.",
    summaryDe:
      "Was unterscheidet die wertvollsten Tech-Unternehmen? Warum Plattformen, soziale Netzwerke, Marktplätze und KI-Produkte alle in einem einzigen Phänomen konvergieren — dem Netzwerkeffekt.",
    summaryFr:
      "Qu'est-ce qui distingue les entreprises technologiques les plus valorisées ? Pourquoi plateformes, réseaux sociaux, marketplaces et produits IA convergent tous vers un même phénomène : l'effet de réseau.",
    tags: ["AI Strategy", "Network Effects", "Competitive Advantage"],
    tagsDe: ["KI-Strategie", "Netzwerkeffekte", "Wettbewerbsvorteil"],
    tagsFr: ["Stratégie IA", "Effets de réseau", "Avantage concurrentiel"],
    author: "Sergii Shelpuk",
    authorRole: "Managing Partner",
    legacyPath: "post/all-you-need-is-a-network-effect-how-great-tech-and-ai-products-compete",
    category: "Business",
    categoryDe: "Wirtschaft",
    categoryFr: "Entreprise",
    heroImage: "network-effect-hero.png",
  },
  {
    slug: "planning-for-a-new-ai-initiative-start-with-the-right-ai-development-culture",
    title: "New AI Initiative? Start with the Right AI Culture",
    titleDe: "Neue KI-Initiative? Erst die richtige KI-Kultur",
    titleFr: "Initiative IA ? Commencez par la culture IA",
    date: "2022-11-28",
    updated: "2026-09-09",
    readTime: "7 min read",
    readTimeDe: "7 Min. Lesezeit",
    readTimeFr: "7 min de lecture",
    summary:
      "Certain facts about AI systems are counterintuitive to conventional engineering wisdom. Four principles that separate AI competitive advantage from regular product development.",
    summaryDe:
      "Bestimmte Fakten über KI-Systeme widersprechen der konventionellen Ingenieursweisheit. Vier Prinzipien, die den KI-Wettbewerbsvorteil von regulärer Produktentwicklung unterscheiden.",
    summaryFr:
      "Certains aspects des systèmes d'IA vont à l'encontre des réflexes d'ingénierie traditionnels. Quatre principes qui distinguent un avantage concurrentiel fondé sur l'IA d'un simple développement produit.",
    tags: ["AI Strategy", "Engineering Culture", "Competitive Advantage"],
    tagsDe: ["KI-Strategie", "Entwicklungskultur", "Wettbewerbsvorteil"],
    tagsFr: ["Stratégie IA", "Culture d’ingénierie", "Avantage concurrentiel"],
    author: "Sergii Shelpuk",
    authorRole: "Managing Partner",
    legacyPath: "post/planning-for-a-new-ai-initiative-start-with-the-right-ai-development-culture",
    category: "Business",
    categoryDe: "Wirtschaft",
    categoryFr: "Entreprise",
    heroImage: "ai-development-culture.jpg",
  },
  {
    slug: "lacking-data-for-machine-learning-models-build-for-the-future-data",
    title: "No Data for Your ML Model? Build for Future Data",
    titleDe: "Zu wenig Daten für ML? Bauen Sie für die Zukunft",
    titleFr: "Pas assez de données ? Visez les données futures",
    date: "2022-11-22",
    updated: "2026-09-09",
    readTime: "5 min read",
    readTimeDe: "5 Min. Lesezeit",
    readTimeFr: "5 min de lecture",
    summary:
      "Why currently available data isn't a prerequisite for building an AI business — and how designing for 'future data' creates a defensible competitive advantage.",
    summaryDe:
      'Warum aktuell verfügbare Daten keine Voraussetzung für den Aufbau eines KI-Unternehmens sind \u2014 und wie das Design für \u201EFuture Data\u201C einen verteidigungsfähigen Wettbewerbsvorteil schafft.',
    summaryFr:
      "Pourquoi les données dont vous disposez aujourd'hui ne sont pas un prérequis absolu pour bâtir un produit IA, et comment concevoir pour les « données futures » crée un avantage concurrentiel défendable.",
    tags: ["AI Strategy", "Machine Learning", "Data Strategy"],
    tagsDe: ["KI-Strategie", "Machine Learning", "Datenstrategie"],
    tagsFr: ["Stratégie IA", "Machine learning", "Stratégie data"],
    author: "Sergii Shelpuk",
    authorRole: "Managing Partner",
    legacyPath: "post/lacking-data-for-machine-learning-models-build-for-the-future-data",
    category: "Business",
    categoryDe: "Wirtschaft",
    categoryFr: "Entreprise",
    heroImage: "ai-virtuous-cycle.png",
  },
  {
    slug: "why-do-you-need-an-ai-strategy-ai-as-a-competitive-advantage",
    title: "Why You Need an AI Strategy",
    titleDe: "Warum Sie eine KI-Strategie brauchen",
    titleFr: "Stratégie IA : l'avantage concurrentiel",
    date: "2022-11-15",
    updated: "2026-09-09",
    readTime: "4 min read",
    readTimeDe: "4 Min. Lesezeit",
    readTimeFr: "4 min de lecture",
    summary:
      "How AI creates sustainable competitive advantages through network effects and data flywheels — illustrated by the Blue River Technology story.",
    summaryDe:
      "Wie KI durch Netzwerkeffekte und Daten-Flywheels nachhaltige Wettbewerbsvorteile schafft — veranschaulicht am Beispiel von Blue River Technology.",
    summaryFr:
      "Comment l'IA crée des avantages concurrentiels durables grâce aux effets de réseau et aux boucles de données, illustré par l'exemple de Blue River Technology.",
    tags: ["AI Strategy", "Competitive Advantage", "Network Effects"],
    tagsDe: ["KI-Strategie", "Wettbewerbsvorteil", "Netzwerkeffekte"],
    tagsFr: ["Stratégie IA", "Avantage concurrentiel", "Effets de réseau"],
    author: "Sergii Shelpuk",
    authorRole: "Managing Partner",
    legacyPath: "post/why-do-you-need-an-ai-strategy-ai-as-a-competitive-advantage",
    category: "Business",
    categoryDe: "Wirtschaft",
    categoryFr: "Entreprise",
    heroImage: "blue-river-technology.png",
  },
  {
    slug: "what-is-your-ai-business-use-case-how-ai-can-help-and-harm-your-strategic-positioning",
    title: "What Is Your AI Business Use Case?",
    titleDe: "Was ist Ihr KI-Business-Use-Case?",
    titleFr: "Quel est votre cas d'usage IA ?",
    date: "2022-12-05",
    updated: "2026-09-09",
    readTime: "6 min read",
    readTimeDe: "8 Min. Lesezeit",
    readTimeFr: "6 min de lecture",
    summary:
      "Why most AI business use case advice leads you astray — and how to distinguish operational tools from genuine competitive advantage.",
    summaryDe:
      "Warum die meisten Ratschläge zu KI-Business-Use-Cases in die Irre führen — und wie Sie operative Werkzeuge von echtem Wettbewerbsvorteil unterscheiden.",
    summaryFr:
      "Pourquoi la plupart des conseils sur les cas d’usage IA orientent dans la mauvaise direction, et comment distinguer un outil opérationnel d'un véritable avantage concurrentiel.",
    tags: ["AI Strategy", "Business Strategy", "Competitive Advantage"],
    tagsDe: ["KI-Strategie", "Geschäftsstrategie", "Wettbewerbsvorteil"],
    tagsFr: ["Stratégie IA", "Stratégie d’entreprise", "Avantage concurrentiel"],
    author: "Sergii Shelpuk",
    authorRole: "Managing Partner",
    legacyPath: "post/what-is-your-ai-business-use-case-how-ai-can-help-and-harm-your-strategic-positioning",
    category: "Business",
    categoryDe: "Wirtschaft",
    categoryFr: "Entreprise",
    heroImage: "ai-business-use-case.jpg",
  },
  {
    slug: "building-a-sustainable-ai-competitive-advantage-four-ai-designs-that-don-t-work",
    title: "Four AI Designs That Don’t Build an Advantage",
    titleDe: "Vier KI-Designs ohne Wettbewerbsvorteil",
    titleFr: "Quatre approches IA qui ne créent aucun avantage",
    date: "2023-05-02",
    updated: "2026-09-09",
    readTime: "3 min read",
    readTimeDe: "5 Min. Lesezeit",
    readTimeFr: "3 min de lecture",
    summary:
      "Not all AI strategies create lasting competitive advantage. Four common AI designs that lead you astray — and what to do instead.",
    summaryDe:
      "Nicht alle KI-Strategien schaffen einen dauerhaften Wettbewerbsvorteil. Vier verbreitete KI-Designs, die in die Irre führen — und was Sie stattdessen tun sollten.",
    summaryFr:
      "Toutes les stratégies IA ne créent pas un avantage concurrentiel durable. Quatre approches fréquentes qui vous égarent, et ce qu'il faut faire à la place.",
    tags: ["AI Strategy", "Competitive Advantage", "Machine Learning"],
    tagsDe: ["KI-Strategie", "Wettbewerbsvorteil", "Machine Learning"],
    tagsFr: ["Stratégie IA", "Avantage concurrentiel", "Machine learning"],
    author: "Sergii Shelpuk",
    authorRole: "Managing Partner",
    legacyPath: "post/building-a-sustainable-ai-competitive-advantage-four-ai-designs-that-don-t-work",
    category: "Business",
    categoryDe: "Wirtschaft",
    categoryFr: "Entreprise",
    heroImage: "ai-designs-dont-work.png",
  },
  {
    slug: "ai-strategies-that-offer-a-sustainable-competitive-advantage-principles-you-should-follow",
    title: "Principles of Sustainable AI Competitive Advantage",
    titleDe: "KI-Strategien für nachhaltigen Wettbewerbsvorteil",
    titleFr: "Stratégies IA : avantage concurrentiel durable",
    date: "2023-05-09",
    updated: "2026-09-09",
    readTime: "6 min read",
    readTimeDe: "8 Min. Lesezeit",
    readTimeFr: "6 min de lecture",
    summary:
      "Six principles to ensure your AI investment builds a defensible competitive advantage — not a commoditised arms race.",
    summaryDe:
      "Sechs Prinzipien, die sicherstellen, dass Ihre KI-Investition einen verteidigungsfähigen Wettbewerbsvorteil aufbaut — und kein kommoditisiertes Wettrüsten.",
    summaryFr:
      "Six principes pour faire en sorte que votre investissement dans l'IA construise un avantage concurrentiel défendable, plutôt qu'une course aux armements banalisée.",
    tags: ["AI Strategy", "Competitive Advantage", "Network Effects"],
    tagsDe: ["KI-Strategie", "Wettbewerbsvorteil", "Netzwerkeffekte"],
    tagsFr: ["Stratégie IA", "Avantage concurrentiel", "Effets de réseau"],
    author: "Sergii Shelpuk",
    authorRole: "Managing Partner",
    legacyPath: "post/ai-strategies-that-offer-a-sustainable-competitive-advantage-principles-you-should-follow",
    category: "Business",
    categoryDe: "Wirtschaft",
    categoryFr: "Entreprise",
    heroImage: "ai-strategies-hero.png",
  },
];

export function getBlogPostBySlug(slug: string): BlogPost | undefined {
  return blogPosts.find((p) => p.slug === slug);
}

/**
 * Resolve one blog post by slug, or name the slug in the failure (CWS-108)
 *
 * The 23 article components identify their post with a ``SLUG`` constant and
 * used to dereference :func:`getBlogPostBySlug` with a non-null assertion —
 * which ``strictNullChecks: false`` erases at compile time, so a typo'd
 * constant reached production as ``Cannot read properties of undefined``
 * and named neither the component nor the slug. This lookup throws the slug
 * into the message, which is what the render-error boundary's report
 * (CWS-5) shows the reader.
 *
 * Args:
 *     slug: The canonical post slug.
 *
 * Returns:
 *     The matching post.
 *
 * Raises:
 *     Error: When no post carries the slug — the message names it.
 */
export function getBlogPostBySlugOrThrow(slug: string): BlogPost {
  const post = getBlogPostBySlug(slug);
  if (!post) {
    throw new Error(
      `Blog post data missing for slug "${slug}" — check the SLUG constant in the page component`,
    );
  }
  return post;
}

/** Get localized field value */
export function getLocalizedField(post: BlogPost, field: 'title' | 'summary' | 'readTime' | 'category', lang: string): string {
  if (lang === 'de') {
    const deKey = `${field}De` as keyof BlogPost;
    return (post[deKey] as string) || post[field];
  }
  if (lang === 'fr') {
    const frKey = `${field}Fr` as keyof BlogPost;
    const localizedValue = post[frKey] as string | undefined;

    if (field === "readTime" && localizedValue === "lecture") {
      const minutes = post.readTime.match(/^\d+/)?.[0];
      return minutes ? `${minutes} min de lecture` : post.readTime;
    }

    return localizedValue || post[field];
  }
  if (lang === "it") {
    const metadata = blogPostItalianMetadata[post.slug];

    if (field === "readTime") {
      const localizedReadTime = metadata?.readTime || post.readTimeIt;
      if (localizedReadTime) {
        return localizedReadTime;
      }

      const minutes = post.readTime.match(/^\d+/)?.[0];
      return minutes ? `${minutes} min di lettura` : post.readTime;
    }

    if (field === "category") {
      return metadata?.category || post.categoryIt || ITALIAN_CATEGORY_MAP[post.category];
    }

    if (field === "title") {
      return metadata?.title || post.titleIt || post.title;
    }

    if (field === "summary") {
      return metadata?.summary || post.summaryIt || post.summary;
    }
  }

  if (lang === "es") {
    const metadata = blogPostSpanishMetadata[post.slug];

    if (field === "readTime") {
      const localizedReadTime = metadata?.readTime;
      if (localizedReadTime) {
        return localizedReadTime;
      }

      const minutes = post.readTime.match(/^\d+/)?.[0];
      return minutes ? `${minutes} min de lectura` : post.readTime;
    }

    if (field === "category") {
      return metadata?.category || SPANISH_CATEGORY_MAP[post.category];
    }

    if (field === "title") {
      return metadata?.title || post.title;
    }

    if (field === "summary") {
      return metadata?.summary || post.summary;
    }
  }

  return post[field];
}

export function getLocalizedTags(post: BlogPost, lang: string): string[] {
  if (lang === "de" && post.tagsDe?.length) {
    return post.tagsDe;
  }

  if (lang === "fr" && post.tagsFr?.length) {
    return post.tagsFr;
  }

  if (lang === "it") {
    return blogPostItalianMetadata[post.slug]?.tags || post.tagsIt || post.tags;
  }

  if (lang === "es") {
    return blogPostSpanishMetadata[post.slug]?.tags || post.tags;
  }

  return post.tags;
}

/**
 * Resolve the date an article's content was last modified.
 *
 * Returns the post's `updated` date when one is recorded, otherwise its
 * publication date. The single source for the sitemap's `<lastmod>` and the
 * BlogPosting JSON-LD `dateModified`, so the two freshness signals agree with
 * each other and with the content data (SEO.md `B15`). For a post never
 * edited after publication the two dates are equal, which is correct — the
 * defect this replaces was the hard-set equality with no mechanism to differ.
 *
 * Args:
 *     post: The blog post to resolve the modification date for.
 *
 * Returns:
 *     The modification date as an ISO calendar date (`YYYY-MM-DD`).
 */
export function getPostModifiedDate(post: BlogPost): string {
  return post.updated ?? post.date;
}
